import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ClientContractTemplateResponse,
  ClientContractTemplateVersionResponse,
} from '@interfaces/companys/contracts/responses/client-contract-template.response';
import { ClientContractType } from '@interfaces/companys/contracts/responses/client-contract.response';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { ClientContractsHttpService } from '@services/http/companys/client-contracts-http.service';
import { RichTextEditorComponent } from '@src/app/shared/components/rich-text-editor/rich-text-editor.component';
import { RichTextVariable } from '@src/app/shared/components/rich-text-editor/rich-text-editor.models';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { Observable, first } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PartnerContractUnsavedChanges } from '../../../../partner/guards/partner-contract-unsaved-changes.guard';
import {
  ContractTemplateVersionListItem,
  PartnerContractTemplateHistoryComponent,
} from '../../../../partner/pages/partner-contract-template/components/partner-contract-template-history/partner-contract-template-history.component';
import {
  CONTRACT_TEXT_LOCKED_MESSAGE,
  backendErrorMessage,
} from '../../../../partner/utils/partner-contract.utils';
import {
  CLIENT_CONTRACT_TYPES,
  clientContractTypeFromQuery,
  clientContractTypeLabel,
  clientContractTypeToQuery,
} from '../../utils/client-contract.utils';

/**
 * Modelos dos contratos de cliente (card 3643), rota `company/contract-templates`
 * (`CLIENTCONTRACT_MANAGE_TEMPLATE`): abas Flat Fee | Gestão Ativa (`?type=ga`), cada uma com o seu
 * modelo versionado. A v1 de cada tipo nasce no backend, do texto padrão, no primeiro `GET`.
 *
 * Mesmo desenho da tela do modelo do contrato de parceria (editor, botão Variável, histórico de
 * versões com "Usar como base"). Salvar exige também estar na lista de editores do texto
 * (`canEditText`); sem isso o modelo fica só leitura, com o aviso de quem pode alterar.
 */
@Component({
  selector: 'app-client-contract-template',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    VaultDateTimePipe,
    RichTextEditorComponent,
    PartnerContractTemplateHistoryComponent,
  ],
  templateUrl: './client-contract-template.component.html',
  styleUrls: ['./client-contract-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientContractTemplateComponent implements OnInit, PartnerContractUnsavedChanges {
  private readonly contractsService = inject(ClientContractsHttpService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };
  readonly breadcrumbs: MenuItem[] = [
    { label: 'Gerenciamento' },
    { label: 'Empresas', routerLink: '/dashboard/company/list' },
    { label: 'Modelos de contrato' },
  ];

  readonly types = CLIENT_CONTRACT_TYPES;
  readonly typeLabel = clientContractTypeLabel;
  /** Exemplos exibidos no aviso (no template, `{{` seria lido como interpolação). */
  readonly variableExample = '{{chave}}';
  readonly blockExample = '[[bloco:economicGroup]] ... [[/bloco:economicGroup]]';
  readonly textLockedMessage = CONTRACT_TEXT_LOCKED_MESSAGE;

  readonly bodyControl = new FormControl<string>('', { nonNullable: true });
  readonly changeNoteControl = new FormControl<string>('', { nonNullable: true });
  readonly viewingBodyControl = new FormControl<string>('', { nonNullable: true });

  readonly type = signal<ClientContractType>('FlatFee');
  readonly current = signal<ClientContractTemplateResponse | null>(null);
  readonly versions = signal<ClientContractTemplateVersionResponse[]>([]);
  readonly variables = signal<RichTextVariable[]>([]);
  readonly viewing = signal<ClientContractTemplateResponse | null>(null);

  readonly loading = signal(true);
  readonly versionsLoading = signal(true);
  readonly saving = signal(false);
  readonly dirty = signal(false);
  readonly historyOpen = signal(true);

  /** Só leitura até o backend dizer que o usuário está na lista de editores do texto. */
  readonly readOnly = computed(() => this.current()?.canEditText !== true);

  ngOnInit(): void {
    this.bodyControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dirty.set(this.bodyControl.dirty));

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.type.set(clientContractTypeFromQuery(params.get('type')));
      this.resetForType();
      this.loadCurrent();
      this.loadVersions();
      this.loadVariables();
    });
  }

  /** Troca de aba. Mudança de query param não passa pelo `canDeactivate`: pergunta aqui. */
  selectType(type: ClientContractType): void {
    if (type === this.type()) return;

    const go = () => {
      this.dirty.set(false);
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { type: clientContractTypeToQuery(type) },
        queryParamsHandling: 'merge',
      });
    };

    const answer = this.confirmLeave();
    if (answer === true) {
      go();
      return;
    }
    (answer as Observable<boolean>)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) go();
      });
  }

  toggleHistory(): void {
    this.historyOpen.update(open => !open);
  }

  /** Clique no histórico: a vigente volta ao editor; uma antiga abre em leitura. */
  openVersion(version: ContractTemplateVersionListItem): void {
    if (version.isCurrent) {
      this.viewing.set(null);
      return;
    }

    this.contractsService
      .getTemplateVersion(version.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: template => {
          if (!template) return;
          this.viewingBodyControl.setValue(template.bodyHtml ?? '');
          this.viewing.set(template);
        },
        error: error =>
          this.toast.showToastError(
            `Não foi possível abrir a versão ${version.version}`,
            backendErrorMessage(error) || 'Clique na versão de novo em instantes.'
          ),
      });
  }

  closeViewing(): void {
    this.viewing.set(null);
  }

  /** Copia o texto da versão antiga para o editor (vira o rascunho da próxima versão). */
  useViewingAsBase(): void {
    const template = this.viewing();
    if (!template || this.readOnly()) return;

    this.bodyControl.setValue(template.bodyHtml ?? '');
    this.bodyControl.markAsDirty();
    this.dirty.set(true);
    if (!this.changeNoteControl.value) {
      this.changeNoteControl.setValue(`Baseada na versão ${template.version}`);
    }
    this.viewing.set(null);
    this.toast.showToastInfo(
      `Texto da versão ${template.version} carregado`,
      'Revise e use "Salvar nova versão" para torná-lo o modelo vigente.'
    );
  }

  canSave(): boolean {
    return (
      !this.readOnly() &&
      this.dirty() &&
      !this.saving() &&
      !this.viewing() &&
      !!this.bodyControl.value.trim()
    );
  }

  save(): void {
    if (!this.canSave()) return;

    this.confirmationService
      .confirm(
        `Salvar nova versão do modelo ${clientContractTypeLabel(this.type())}?`,
        'Os contratos já gerados não mudam. Os próximos usarão esta versão.',
        'default',
        'Cancelar',
        'Salvar nova versão'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) this.persist();
      });
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      this.save();
    }
  }

  confirmLeave(): boolean | Observable<boolean> {
    if (!this.dirty()) return true;
    return this.confirmationService
      .confirm(
        'Sair sem salvar?',
        'As mudanças no modelo ainda não foram salvas e serão perdidas.',
        'danger',
        'Continuar editando',
        'Sair sem salvar'
      )
      .pipe(first());
  }

  @HostListener('window:beforeunload', ['$event'])
  protected onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.dirty()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  private persist(): void {
    this.saving.set(true);
    const changeNote = this.changeNoteControl.value.trim();
    const sentBody = this.bodyControl.value;

    this.contractsService
      .saveTemplateVersion({
        type: this.type(),
        bodyHtml: sentBody,
        changeNote: changeNote || null,
      })
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: template => {
          this.toast.showToastSuccess(
            template ? `Versão ${template.version} salva` : 'Nova versão salva',
            'Os próximos contratos gerados já usam este texto.'
          );
          // Se a pessoa continuou digitando durante o salvamento, não sobrescrever o editor.
          if (template && this.bodyControl.value === sentBody) {
            this.applyCurrent(template, { keepBody: true });
          } else if (template) {
            this.current.set(template);
          }
          this.changeNoteControl.setValue('');
          this.loadVersions();
        },
        error: error =>
          this.toast.showToastError(
            'Não foi possível salvar a nova versão',
            backendErrorMessage(error) || 'O texto continua aqui; tente salvar de novo.'
          ),
      });
  }

  // As três cargas guardam o tipo pedido e descartam a resposta se a aba já mudou: uma resposta atrasada de
  // Flat Fee não pode cair na aba Gestão Ativa (o "Salvar nova versão" gravaria no tipo errado).
  private loadCurrent(): void {
    const type = this.type();
    this.loading.set(true);
    this.contractsService
      .getCurrentTemplate(type)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: template => {
          if (template && type === this.type()) this.applyCurrent(template);
        },
        error: error =>
          this.toast.showToastError(
            `Não foi possível carregar o modelo ${clientContractTypeLabel(this.type())}`,
            backendErrorMessage(error) || 'Recarregue a página para tentar de novo.'
          ),
      });
  }

  private loadVersions(): void {
    const type = this.type();
    this.versionsLoading.set(true);
    this.contractsService
      .getTemplateVersions(type)
      .pipe(
        finalize(() => this.versionsLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: list => {
          if (type === this.type())
            this.versions.set([...list].sort((a, b) => b.version - a.version));
        },
        error: () => this.versions.set([]),
      });
  }

  private loadVariables(): void {
    const type = this.type();
    this.contractsService
      .getVariables(type)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: list => {
          if (type === this.type()) this.variables.set(list);
        },
        error: () => this.variables.set([]),
      });
  }

  private resetForType(): void {
    this.current.set(null);
    this.versions.set([]);
    this.variables.set([]);
    this.viewing.set(null);
    this.changeNoteControl.setValue('');
    this.bodyControl.setValue('', { emitEvent: false });
    this.bodyControl.markAsPristine();
    this.dirty.set(false);
  }

  /** Põe o modelo no editor. `keepBody` evita regravar o corpo (preserva cursor e desfazer). */
  private applyCurrent(
    template: ClientContractTemplateResponse,
    options: { keepBody?: boolean } = {}
  ): void {
    this.current.set(template);
    if (!options.keepBody) {
      this.bodyControl.setValue(template.bodyHtml ?? '', { emitEvent: false });
    }
    this.bodyControl.markAsPristine();
    this.dirty.set(false);
  }
}
